import React, { useState, useEffect, Suspense } from 'react';
import { newMediaGalleryPropTypes, newMediaGalleryDefaultProps } from './propTypes';
import Thumbnails from './subcomponents/Thumbnails';
import MainImage from './subcomponents/MainImage';
import {
  handleThumbnailEvent,
  handleHideOverlay,
  handleMainImageClick,
  organizeThumbnails,
} from './utils/mediaGalleryUtils';
import {
  MEDIA_GALLERY_CONTAINER_HEIGHT,
  MEDIA_GALLERY_CONTAINER_MAXHEIGHT,
  MEDIA_GALLERY_CONTAINER_MAXWIDTH,
  MEDIA_GALLERY_THUMBNAILS_WIDTH
} from './constants';
import { mediaClick } from '../../../publisher';

const NewMediaGalleryOverlay = React.lazy(() => import(
  /* webpackChunkName: "new-media-gallery-overlay" */'./NewMediaGalleryOverlay')
);

/**
 * NewMediaGallery component provides an enhanced media gallery experience
 * with main image display, thumbnail navigation, and overlay functionality.
 * @component
 * @param {Object} props - The component props
 * @param {Object} [props.collection] - Collection data for the product
 * @param {Object} props.media - Media data containing main images and thumbnails
 * @param {Array} props.media.main - Array of main image objects
 * @param {Array} props.media.thumbnails - Array of thumbnail objects
 * @param {Object} [props.overrideData] - Override data for labels and branding
 * @param {string} [props.overrideData.galleryLabel] - Custom gallery label
 * @param {string} [props.overrideData.brandNameLabel] - Custom brand name
 * @param {boolean} [props.hideProductImagesLabel] - Whether to hide product images label
 * @param {Object} props.product - Product information
 * @param {string} [props.product.itemId] - Product item ID
 * @param {Object} [props.product.identifiers] - Product identifiers (brand, label, etc.)
 * @param {string} [props.configuredProductLabel] - Configured product label
 * @param {boolean} [props.showAvataarAR=false] - Whether to show Avataar AR functionality
 * @param {boolean} [props.showAvataarDefault=false] - Whether to show default Avataar view
 * @param {number} [props.augmentedRealityIndex=0] - Index of AR asset in media array
 * @param {number} [props.totalThumbnailsToShow] - Maximum number of thumbnails to display
 * @param {Function} [props.onAvataarButtonClick] - Callback for Avataar button interactions
 * @param {Array} [props.mainSuperSkuHover] - SuperSku hover media data
 * @param {string} [props.mainImageFetchPriority='auto'] - Fetch priority for main image
 * @param {string} [props.isLoading] - Loading state
 *
 * @returns {JSX.Element} The rendered NewMediaGallery component
 */
const NewMediaGallery = (props) => {
  const {
    collection, media, overrideData, hideProductImagesLabel, product, configuredProductLabel,
    showAvataarAR, showAvataarDefault, augmentedRealityIndex, totalThumbnailsToShow,
    onAvataarButtonClick, mainSuperSkuHover, mainImageFetchPriority, isLoading
  } = props;

  const [mainImage, setMainImage] = useState(null);
  const [overlayOpened, setOverlayOpened] = useState(false);
  const [thumbnails, setThumbnails] = useState([]);
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(0);
  const [prevMedia, setPrevMedia] = useState(null);
  const [prevMainSuperSkuHover, setPrevMainSuperSkuHover] = useState(null);
  const [prevShowAvataarAR, setPrevShowAvataarAR] = useState(null);
  const [overlayInitialType, setOverlayInitialType] = useState('IMAGE');
  const [overlayInitialId, setOverlayInitialId] = useState(null);
  const [overlaySelectedIndex, setOverlaySelectedIndex] = useState(0);
  const [originalThumbnails, setOriginalThumbnails] = useState([]);
  const [originalMainImages, setOriginalMainImages] = useState([]);

  useEffect(() => {
    const { thumbnails: mediaThumbnails = [], main: mainImages = [] } = media;

    setOriginalThumbnails(mediaThumbnails);
    setOriginalMainImages(mainImages);
    setThumbnails(mediaThumbnails);

    if (showAvataarAR) {
      setSelectedThumbnailIndex(augmentedRealityIndex);
      setOverlayOpened(true);
    }
  }, [media, showAvataarAR, augmentedRealityIndex, totalThumbnailsToShow]);

  const organizedThumbs = organizeThumbnails(originalThumbnails);
  const organizedMedia = organizeThumbnails(originalMainImages);

  return (
    <>
      <div
        className="mediagallery sui-flex sui-flex-col sui-gap-6 sui-w-full sui-mx-auto"
        style={{ maxWidth: MEDIA_GALLERY_CONTAINER_MAXWIDTH }}
        data-component="NewMediaGallery"
        data-testid="NewMediaGallery"
      >
        <div
          className="sui-flex sui-items-center sui-justify-center sui-w-full sui-mx-auto"
          style={{
            height: MEDIA_GALLERY_CONTAINER_HEIGHT,
            maxHeight: MEDIA_GALLERY_CONTAINER_MAXHEIGHT
          }}
        >
          <MainImage
            mainImage={mainImage}
            selectedThumbnailIndex={selectedThumbnailIndex}
            media={media}
            identifiers={product?.identifiers}
            mainImageFetchPriority={mainImageFetchPriority}
            overrideData={overrideData}
            showAvataarDefault={showAvataarDefault}
            onMainImageClick={(thumbnailType) => {
              handleMainImageClick({
                thumbnailType,
                augmentedRealityIndex,
                selectedThumbnailIndex,
                mainImage,
                setOverlayOpened,
                organizedThumbs,
                originalThumbnails,
                setOverlaySelectedIndex,
                setOverlayInitialType,
                setOverlayInitialId,
                mediaClick,
                setSelectedThumbnailIndex,
              });
            }}
          />
        </div>
        <div className="sui-mt-4" style={{ maxWidth: MEDIA_GALLERY_THUMBNAILS_WIDTH }}>
          <Thumbnails
            thumbnailList={organizedThumbs}
            mainImageList={organizedMedia}
            selectedThumbnailIndex={selectedThumbnailIndex}
            totalThumbnailsToShow={totalThumbnailsToShow}
            originalThumbnailCount={originalThumbnails.length}
            onThumbnailEvent={(thumbnailList, images, image, thumbnail, eventType) => {
              return handleThumbnailEvent({
                thumbnailList: organizedThumbs,
                images: organizedMedia,
                thumbnail,
                eventType,
                totalThumbnailsToShow,
                originalThumbnailCount: originalThumbnails.length,
                mainImage,
                setSelectedThumbnailIndex,
                setMainImage,
                setThumbnails: setOriginalThumbnails,
                setOverlayOpened,
                setOverlayInitialType,
                setOverlayInitialId,
                augmentedRealityIndex
              });
            }}
          />
        </div>
      </div>
      {
        overlayOpened && (
          <Suspense fallback={<div />}>
            <NewMediaGalleryOverlay
              handleClose={() => handleHideOverlay(setOverlayOpened, onAvataarButtonClick)}
              overlayOpened={overlayOpened}
              collection={collection}
              media={media}
              product={product}
              mainAsset={mainImage}
              selectedImageIndex={overlaySelectedIndex}
              thumbnails={originalThumbnails}
              overrideData={overrideData}
              hideProductImagesLabel={hideProductImagesLabel}
              configuredProductLabel={configuredProductLabel}
              videoAnalyticsData={{ position: (overlaySelectedIndex + 1).toString() }}
              itemId={product?.itemId || ''}
              showAvataarDefault={showAvataarDefault}
              showAvataarAR={showAvataarAR}
              initialType={overlayInitialType}
              initialId={overlayInitialId}
            />
          </Suspense>
        )
      }
    </>
  );
};

NewMediaGallery.propTypes = newMediaGalleryPropTypes;
NewMediaGallery.defaultProps = newMediaGalleryDefaultProps;

export default NewMediaGallery;